// src/services/adminPaiement.js
//
// Domaine Admin — Paiement (façade Worker Admin → Worker Paiement)
// Aucun appel direct au Worker Paiement depuis le navigateur.
//
// Routes strictement alignées sur le contrat Worker Admin.
// Aucune route hors contrat.

import { apiFetch, buildQuery } from './adminHttp';

/* ============================================================
 * PAIEMENTS
 * GET /admin/paiements
 * ============================================================ */
export async function getPaiements(params = {}) {
  return apiFetch(`/admin/paiements${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ============================================================
 * JOURNAL DES PAIEMENTS
 * GET /admin/paiements/journal
 * ============================================================ */
export async function getJournalPaiements(params = {}) {
  return apiFetch(`/admin/paiements/journal${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ============================================================
 * RETRAITS
 * GET /admin/retraits
 * ============================================================ */
export async function getRetraits(params = {}) {
  return apiFetch(`/admin/retraits${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ============================================================
 * TRAITEMENT D'UN RETRAIT
 * POST /admin/retraits/traiter
 *
 * Contrat réel (Worker Admin) :
 *   Body : { "idRetrait": string, "nouveauStatut": string }
 * Aucune traduction de statut côté service.
 * ============================================================ */
export async function traiterRetrait(idRetrait, nouveauStatut) {
  return apiFetch('/admin/retraits/traiter', {
    method: 'POST',
    body: JSON.stringify({
      idRetrait,
      nouveauStatut,
    }),
  });
}

/* ============================================================
 * RECALCUL DES NIVEAUX DSI
 * POST /admin/niveaux/recalculer
 * ============================================================ */
export async function recalculerNiveaux() {
  return apiFetch('/admin/niveaux/recalculer', {
    method: 'POST',
  });
}

/* ============================================================
 * SUPERVISION FINANCIÈRE
 * GET /admin/supervision/indicateurs
 * ============================================================ */
export async function getSupervisionIndicateurs(params = {}) {
  return apiFetch(`/admin/supervision/indicateurs${buildQuery(params)}`, {
    method: 'GET',
  });
}

/* ============================================================
 * RÉCONCILIATION
 * POST /admin/reconciliation
 * Body contrat : {} (aucun champ métier)
 *
 * Résultats attendus (non masqués — via apiFetch / adminHttp) :
 *   - HTTP 200 + { status: "termine", ... }  → retour normal (resolve)
 *   - HTTP 409 + { status: "deja_en_cours" } → reject Error
 *       err.status === 409
 *       err.body.status === "deja_en_cours"   ← état métier identifiable
 *   - HTTP 500 + { status: "erreur", ... }   → reject Error
 *       err.status === 500
 *       err.body.status === "erreur"
 *
 * La couche appelante doit inspecter err.status et err.body
 * (pas seulement err.message) pour distinguer ces cas.
 * ============================================================ */
export async function lancerReconciliation() {
  return apiFetch('/admin/reconciliation', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/* ============================================================
 * CODES RÉCOMPENSE
 * Routes Worker Admin (façade → Worker Paiement) :
 *   POST /admin/codes/lister
 *   POST /admin/codes/creer
 *   POST /admin/codes/modifier
 *   POST /admin/codes/set-actif
 *   POST /admin/codes/migrer-depuis-sheets
 * ============================================================ */

/** Liste les codes. Body optionnel : { limit? } (défaut 200, plafond 500 côté backend). */
export async function listerCodes(limit) {
  const body = {};
  if (limit !== undefined && limit !== null && limit !== '') {
    body.limit = Number(limit);
  }
  return apiFetch('/admin/codes/lister', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Créer un code.
 * Body : { code, montantDsi, utilisationsMax?, dateExpiration?, heureExpiration?, actif? }
 * Ne pas envoyer utilisationsActuelles.
 */
export async function creerCode(payload) {
  return apiFetch('/admin/codes/creer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Modification partielle.
 * Body : { code, ...champs modifiés }
 * Interdit : utilisationsActuelles
 */
export async function modifierCode(payload) {
  return apiFetch('/admin/codes/modifier', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Activer / désactiver. Body : { code, actif: boolean } */
export async function setCodeActif(code, actif) {
  return apiFetch('/admin/codes/set-actif', {
    method: 'POST',
    body: JSON.stringify({ code, actif: Boolean(actif) }),
  });
}

/** Migration initiale Sheets → D1. Aucun body métier. */
export async function migrerCodesDepuisSheets() {
  return apiFetch('/admin/codes/migrer-depuis-sheets', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
